/**
 * RTWO Native Currency Engine — Engine Implementation
 * Phase 14.5 | Module ID: RTWO-NATV-001
 * Version: 1.0.0-phase14.5
 *
 * Coordinates all Native Currency Engine services through dependency injection,
 * in-memory repositories, adapter registries, and the internal event bus.
 *
 * Truthful Data Policy:
 * - No fake RTWO supply, market cap, price, treasury balance, validator rewards,
 *   staking supply, or allocations.
 * - RTWO currency is NOT claimed as live.
 * - All operations return placeholder results until Genesis Configuration
 *   and Ledger Connection are established.
 *
 * Source: Native Currency Engine (RTWO-NATV-001)
 */

import { NATV_ENGINE_VERSION } from './domain-models';
import type {
  NATVServiceDescriptor,
  NATVServiceRegistry,
  NATVReadinessReport,
} from './service-interfaces';
import { createNATVEngineServices } from './services';

export class RTWONativeCurrencyEngine {
  private static _instance: RTWONativeCurrencyEngine | null = null;

  readonly version = NATV_ENGINE_VERSION;

  readonly services: NATVServiceRegistry;

  private constructor() {
    const { services } = createNATVEngineServices();

    this.services = {
      nativeCurrencyRegistry: services.nativeCurrencyRegistry,
      monetaryPolicyService: services.monetaryPolicyService,
      supplyStateService: services.supplyStateService,
      allocationPolicyService: services.allocationPolicyService,
      feePolicyService: services.feePolicyService,
      emissionScheduleService: services.emissionScheduleService,
      economicParameterService: services.economicParameterService,
      nativeCurrencyEventPublisher: services.nativeCurrencyEventPublisher,
      nativeCurrencyValidationService: services.nativeCurrencyValidationService,
      nativeCurrencyReadinessService: services.nativeCurrencyReadinessService,
    };
  }

  static getInstance(): RTWONativeCurrencyEngine {
    if (!RTWONativeCurrencyEngine._instance) {
      RTWONativeCurrencyEngine._instance = new RTWONativeCurrencyEngine();
    }
    return RTWONativeCurrencyEngine._instance;
  }

  async initialize(): Promise<void> {
    const serviceList = Object.values(this.services);
    await Promise.all(serviceList.map((service) => service.initialize()));
  }

  getServiceDescriptors(): NATVServiceDescriptor[] {
    return Object.values(this.services).map((service) => service.getDescriptor());
  }

  getReadinessReport(): NATVReadinessReport {
    return this.services.nativeCurrencyReadinessService.getReadinessReport();
  }
}

export const getNativeCurrencyEngine = (): RTWONativeCurrencyEngine =>
  RTWONativeCurrencyEngine.getInstance();

export default RTWONativeCurrencyEngine;
